import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { clientService } from '../services/clientService';
import { useHeader } from '../contexts/HeaderContext';
import LoadingSpinner from '../components/LoadingSpinner';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setTitle } = useHeader();

  const { data: client, isLoading, error } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientService.getClient(parseInt(id!)),
    enabled: !!id,
  });

  React.useEffect(() => {
    if (client) {
      setTitle(client.name);
    }
  }, [client, setTitle]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Client Not Found</h3>
        <p className="mt-1 text-sm text-gray-500">
          The client you are looking for does not exist or you do not have access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
        <p className="mt-1 text-sm text-gray-500">Client Details</p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Client Information</h3>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <p className="mt-1 text-sm text-gray-900">{client.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <p className="mt-1 text-sm text-gray-900">{client.email || 'Not provided'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <p className="mt-1 text-sm text-gray-900">{client.phone || 'Not provided'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Website</label>
              <p className="mt-1 text-sm text-gray-900">
                {client.website ? (
                  <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500">
                    {client.website}
                  </a>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Industry</label>
              <p className="mt-1 text-sm text-gray-900">{client.industry || 'Not specified'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Size</label>
              <p className="mt-1 text-sm text-gray-900">{client.companySize || 'Not specified'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${client.status === 'active'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
                }`}>
                {client.status}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Created</label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(client.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Goals */}
      {client.goals && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Client Goals</h3>
          </div>
          <div className="px-6 py-4">
            <pre className="text-sm text-gray-900 whitespace-pre-wrap">
              {JSON.stringify(client.goals, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Evaluations */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Evaluations</h3>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-gray-500">
            {client._count?.assessments || 0} evaluations completed
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Evaluation history will be displayed here
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
